import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ResetZoomIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  TouchApp as TouchAppIcon,
} from '@mui/icons-material';
import { TransformWrapper, TransformComponent, type ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { api, type AcceleratorHotspot } from '../../services/api';
import AcceleratorProfileDialog from './AcceleratorProfileDialog';
import AcceleratorHotspotForm, { type AcceleratorRect } from './AcceleratorHotspotForm';

const POSTER_URL =
  'https://firebasestorage.googleapis.com/v0/b/transformation-office.firebasestorage.app/o/misc%2Fsuper-accelerator-20261026.jpeg?alt=media&token=ae31bdee-9176-416d-944b-0729d55d24be';
// Areas are stored against this key. Swapping POSTER_URL for a new poster
// needs a new key too, or the old rectangles land on the wrong faces.
const POSTER_KEY = 'super-accelerator-20261026';

// The poster is 1600×900; the real ratio replaces this once the image loads,
// so the frame always matches the picture and percentage areas line up.
const POSTER_ASPECT = 16 / 9;
const MAX_SCALE = 5;
/** A pointer that travels further than this between down and up was a pan, not a tap. */
const TAP_SLOP_PX = 6;
const MIN_AREA_PERCENT = 0.5;
const GOLD = '#f6c453';
/** Corner controls fade out after this long without the pointer near them. */
const CONTROLS_IDLE_MS = 3000;
/** How close (px) the mouse or a tap must come to a hidden control to bring it back. */
const CONTROLS_REVEAL_PX = 80;

const toolButtonSx = {
  color: '#fff',
  width: 36,
  height: 36,
  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.14)' },
  '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.35)' },
};

const glassSx = {
  bgcolor: 'rgba(10, 10, 20, 0.72)',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  backdropFilter: 'blur(6px)',
  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
};

interface Props {
  open: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

type Point = { x: number; y: number };

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function rectFromPoints(a: Point, b: Point): AcceleratorRect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

function rectOf(hotspot: AcceleratorHotspot): AcceleratorRect {
  return { x: hotspot.x, y: hotspot.y, width: hotspot.width, height: hotspot.height };
}

function isNear(el: HTMLElement | null, clientX: number, clientY: number): boolean {
  const box = el?.getBoundingClientRect();
  if (!box) return false;
  return (
    clientX >= box.left - CONTROLS_REVEAL_PX &&
    clientX <= box.right + CONTROLS_REVEAL_PX &&
    clientY >= box.top - CONTROLS_REVEAL_PX &&
    clientY <= box.bottom + CONTROLS_REVEAL_PX
  );
}

/**
 * Visible when the dialog opens, gone after CONTROLS_IDLE_MS. A mouse resting
 * near the control keeps it up; a tap near it shows it for another spell.
 */
function useAutoHide(enabled: boolean) {
  const [visible, setVisible] = useState(true);
  const timerRef = useRef<number | undefined>(undefined);
  const hoveringRef = useRef(false);

  const scheduleHide = useCallback(() => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setVisible(false), CONTROLS_IDLE_MS);
  }, []);

  const reveal = useCallback(() => {
    setVisible(true);
    if (!hoveringRef.current) scheduleHide();
  }, [scheduleHide]);

  const setHovering = useCallback(
    (near: boolean) => {
      if (near === hoveringRef.current) return;
      hoveringRef.current = near;
      if (near) {
        window.clearTimeout(timerRef.current);
        setVisible(true);
      } else {
        scheduleHide();
      }
    },
    [scheduleHide],
  );

  useEffect(() => {
    hoveringRef.current = false;
    if (enabled) {
      setVisible(true);
      scheduleHide();
    }
    return () => window.clearTimeout(timerRef.current);
  }, [enabled, scheduleHide]);

  return { visible, reveal, setHovering };
}

function fadeSx(visible: boolean) {
  return { opacity: visible ? 1 : 0, pointerEvents: visible ? 'auto' : 'none', transition: 'opacity 0.3s ease' } as const;
}

export default function SuperAcceleratorPosterDialog({ open, onClose, isAdmin = false }: Props) {
  const [scale, setScale] = useState(1);
  const [aspect, setAspect] = useState(POSTER_ASPECT);
  const [hotspots, setHotspots] = useState<AcceleratorHotspot[]>([]);
  const [selected, setSelected] = useState<AcceleratorHotspot | null>(null);

  // Admin editing
  const [editing, setEditing] = useState(false);
  const [drawStart, setDrawStart] = useState<Point | null>(null);
  const [drawCurrent, setDrawCurrent] = useState<Point | null>(null);
  const [form, setForm] = useState<{ hotspot: AcceleratorHotspot | null; rect: AcceleratorRect } | null>(null);
  const [redrawTarget, setRedrawTarget] = useState<AcceleratorHotspot | null>(null);

  const layerRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const tapOriginRef = useRef<Point | null>(null);
  const editControlsRef = useRef<HTMLDivElement>(null);
  const zoomControlsRef = useRef<HTMLDivElement>(null);
  const editControls = useAutoHide(open);
  const zoomControls = useAutoHide(open);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    api
      .listAcceleratorHotspots(POSTER_KEY)
      .then((rows) => !cancelled && setHotspots(rows))
      .catch((error) => console.warn('[accelerator poster] areas failed to load', error));
    return () => {
      cancelled = true;
    };
  }, [open]);

  const toPercent = useCallback((clientX: number, clientY: number): Point | null => {
    const box = layerRef.current?.getBoundingClientRect();
    if (!box || !box.width || !box.height) return null;
    return {
      x: clampPercent(((clientX - box.left) / box.width) * 100),
      y: clampPercent(((clientY - box.top) / box.height) * 100),
    };
  }, []);

  const toggleEditing = () => {
    transformRef.current?.resetTransform(0);
    setEditing((value) => !value);
    setDrawStart(null);
    setDrawCurrent(null);
    setRedrawTarget(null);
  };

  // ── Drawing (edit mode) ────────────────────────────────────────────
  const handleLayerPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!editing || event.button !== 0) return;
    const point = toPercent(event.clientX, event.clientY);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrawStart(point);
    setDrawCurrent(point);
  };

  const handleLayerPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drawStart) return;
    const point = toPercent(event.clientX, event.clientY);
    if (point) setDrawCurrent(point);
  };

  const handleLayerPointerUp = () => {
    if (!drawStart || !drawCurrent) return;
    const rect = rectFromPoints(drawStart, drawCurrent);
    setDrawStart(null);
    setDrawCurrent(null);
    if (rect.width < MIN_AREA_PERCENT || rect.height < MIN_AREA_PERCENT) return;
    setForm({ hotspot: redrawTarget, rect });
    setRedrawTarget(null);
  };

  // ── Hotspot taps ───────────────────────────────────────────────────
  const handleAreaPointerDown = (event: React.PointerEvent) => {
    tapOriginRef.current = { x: event.clientX, y: event.clientY };
    // In edit mode a press on an existing area edits it instead of drawing.
    if (editing) event.stopPropagation();
  };

  const handleAreaClick = (event: React.MouseEvent, hotspot: AcceleratorHotspot) => {
    const origin = tapOriginRef.current;
    tapOriginRef.current = null;
    if (origin && Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > TAP_SLOP_PX) return;
    if (editing) {
      if (!redrawTarget) setForm({ hotspot, rect: rectOf(hotspot) });
      return;
    }
    setSelected(hotspot);
  };

  const handleSaved = (saved: AcceleratorHotspot) => {
    setHotspots((rows) => {
      const exists = rows.some((row) => row.id === saved.id);
      return exists ? rows.map((row) => (row.id === saved.id ? saved : row)) : [...rows, saved];
    });
    setForm(null);
  };

  const handleDeleted = (id: number) => {
    setHotspots((rows) => rows.filter((row) => row.id !== id));
    setForm(null);
  };

  // ── Auto-hiding corner controls ────────────────────────────────────
  const corners = [
    { ref: editControlsRef, controls: editControls },
    { ref: zoomControlsRef, controls: zoomControls },
  ];

  const handleRevealMove = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    corners.forEach(({ ref, controls }) => controls.setHovering(isNear(ref.current, event.clientX, event.clientY)));
  };

  const handleRevealDown = (event: React.PointerEvent) => {
    corners.forEach(({ ref, controls }) => {
      if (isNear(ref.current, event.clientX, event.clientY)) controls.reveal();
    });
  };

  const handleRevealLeave = () => corners.forEach(({ controls }) => controls.setHovering(false));

  const draftRect = drawStart && drawCurrent ? rectFromPoints(drawStart, drawCurrent) : null;
  const withoutVideo = hotspots.filter((h) => !h.hasVideo).length;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        TransitionProps={{
          onExited: () => {
            setScale(1);
            setEditing(false);
            setRedrawTarget(null);
          },
        }}
        PaperProps={{
          // Capture phase: the zoom/pan layer swallows pointer events before they bubble.
          onPointerMoveCapture: handleRevealMove,
          onPointerDownCapture: handleRevealDown,
          onPointerLeave: handleRevealLeave,
          sx: {
            m: 0,
            width: `min(96vw, calc((100dvh - 48px) * ${aspect}))`,
            aspectRatio: `${aspect}`,
            maxHeight: 'none',
            bgcolor: '#0b0b14',
            borderRadius: { xs: 1.5, sm: 2 },
            overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.7)',
          },
        }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.85)' } } }}
      >
        <TransformWrapper
          ref={transformRef}
          initialScale={1}
          minScale={1}
          maxScale={MAX_SCALE}
          centerOnInit
          limitToBounds
          centerZoomedOut
          disabled={editing}
          wheel={{ step: 0.1 }}
          doubleClick={{ step: 0.7 }}
          onTransformed={(_, state) => setScale(state.scale)}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <TransformComponent
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                  cursor: editing ? 'crosshair' : scale > 1.01 ? 'grab' : 'zoom-in',
                }}
                contentStyle={{ width: '100%', height: '100%' }}
              >
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img
                    data-magnify-image="super-accelerator-poster"
                    src={POSTER_URL}
                    alt="Super App Accelerators"
                    draggable={false}
                    onLoad={(event) => {
                      const { naturalWidth, naturalHeight } = event.currentTarget;
                      if (naturalWidth && naturalHeight) setAspect(naturalWidth / naturalHeight);
                    }}
                    style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none' }}
                  />
                  <Box
                    ref={layerRef}
                    onPointerDown={handleLayerPointerDown}
                    onPointerMove={handleLayerPointerMove}
                    onPointerUp={handleLayerPointerUp}
                    onPointerCancel={() => {
                      setDrawStart(null);
                      setDrawCurrent(null);
                    }}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      touchAction: editing ? 'none' : 'auto',
                      '@keyframes acceleratorAreaPing': {
                        '0%': { borderColor: 'transparent', boxShadow: 'none' },
                        '35%': { borderColor: GOLD, boxShadow: `0 0 18px ${GOLD}AA, inset 0 0 12px ${GOLD}55` },
                        '100%': { borderColor: 'transparent', boxShadow: 'none' },
                      },
                    }}
                  >
                    {hotspots.map((hotspot, index) => (
                      <Tooltip
                        key={hotspot.id}
                        title={editing ? '' : hotspot.agent.name}
                        placement="top"
                        arrow
                        disableTouchListener
                      >
                        <Box
                          component="button"
                          type="button"
                          aria-label={`View ${hotspot.agent.name || hotspot.agent.ceaNumber}`}
                          onPointerDown={handleAreaPointerDown}
                          onClick={(event: React.MouseEvent) => handleAreaClick(event, hotspot)}
                          sx={{
                            position: 'absolute',
                            left: `${hotspot.x}%`,
                            top: `${hotspot.y}%`,
                            width: `${hotspot.width}%`,
                            height: `${hotspot.height}%`,
                            p: 0,
                            m: 0,
                            cursor: 'pointer',
                            borderRadius: '12px',
                            background: 'transparent',
                            transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
                            ...(editing
                              ? {
                                  border: `2px dashed ${hotspot.hasVideo ? GOLD : '#ff8a80'}`,
                                  bgcolor: redrawTarget?.id === hotspot.id ? 'rgba(255, 138, 128, 0.25)' : 'rgba(246, 196, 83, 0.12)',
                                }
                              : {
                                  border: '2px solid transparent',
                                  animation: `acceleratorAreaPing 1.4s ease-out ${0.4 + index * 0.04}s 2`,
                                  '&:hover, &:focus-visible': {
                                    outline: 'none',
                                    borderColor: GOLD,
                                    bgcolor: 'rgba(246, 196, 83, 0.12)',
                                    boxShadow: `0 0 18px ${GOLD}AA, inset 0 0 12px ${GOLD}55`,
                                  },
                                }),
                          }}
                        >
                          {editing && (
                            <Typography
                              component="span"
                              sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                px: 0.5,
                                fontSize: 10,
                                fontWeight: 700,
                                lineHeight: 1.6,
                                color: '#fff',
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                borderBottomLeftRadius: 10,
                                borderBottomRightRadius: 10,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {hotspot.agent.name || hotspot.agent.ceaNumber}
                              {!hotspot.hasVideo && ' · no video'}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    ))}
                    {draftRect && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${draftRect.x}%`,
                          top: `${draftRect.y}%`,
                          width: `${draftRect.width}%`,
                          height: `${draftRect.height}%`,
                          border: `2px solid ${GOLD}`,
                          borderRadius: '12px',
                          bgcolor: 'rgba(246, 196, 83, 0.2)',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </TransformComponent>

              <IconButton
                onClick={onClose}
                aria-label="Close"
                size="small"
                sx={{
                  position: 'absolute',
                  top: { xs: 6, sm: 12 },
                  right: { xs: 6, sm: 12 },
                  bgcolor: 'var(--color-accent)',
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.45)',
                  '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(1.1)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              {isAdmin && (
                <Box
                  ref={editControlsRef}
                  onFocusCapture={editControls.reveal}
                  sx={{
                    ...fadeSx(editing || editControls.visible),
                    position: 'absolute',
                    top: { xs: 6, sm: 12 },
                    left: { xs: 6, sm: 12 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    maxWidth: 'calc(100% - 80px)',
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={toggleEditing}
                    startIcon={editing ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                    sx={{
                      flexShrink: 0,
                      borderRadius: 999,
                      fontWeight: 700,
                      color: editing ? '#1a1300' : '#fff',
                      bgcolor: editing ? GOLD : 'rgba(10, 10, 20, 0.72)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      '&:hover': { bgcolor: editing ? GOLD : 'rgba(10, 10, 20, 0.9)' },
                    }}
                  >
                    {editing ? 'Done' : 'Edit areas'}
                  </Button>
                  {editing && (
                    <Typography
                      variant="caption"
                      sx={{ ...glassSx, color: '#fff', px: 1.25, py: 0.5, borderRadius: 999, lineHeight: 1.4 }}
                    >
                      {redrawTarget
                        ? `Drag a new area for ${redrawTarget.agent.name || redrawTarget.agent.ceaNumber}`
                        : `Drag over a portrait to tag an agent · tap an area to edit · ${hotspots.length} tagged${
                            withoutVideo ? `, ${withoutVideo} without video` : ''
                          }`}
                    </Typography>
                  )}
                </Box>
              )}

              {!editing && hotspots.length > 0 && (
                <Box
                  sx={{
                    ...glassSx,
                    position: 'absolute',
                    bottom: { xs: 6, sm: 14 },
                    left: { xs: 6, sm: 14 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 999,
                    color: '#fff',
                    pointerEvents: 'none',
                  }}
                >
                  <TouchAppIcon sx={{ fontSize: 16, color: GOLD }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Tap an accelerator to see their story
                  </Typography>
                </Box>
              )}

              {!editing && (
                <Box
                  ref={zoomControlsRef}
                  onFocusCapture={zoomControls.reveal}
                  sx={{
                    ...glassSx,
                    ...fadeSx(zoomControls.visible),
                    position: 'absolute',
                    bottom: { xs: 6, sm: 14 },
                    right: { xs: 6, sm: 14 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 999,
                  }}
                >
                  <Tooltip title="Zoom out">
                    <span>
                      <IconButton aria-label="Zoom out" size="small" onClick={() => zoomOut()} disabled={scale <= 1.01} sx={toolButtonSx}>
                        <ZoomOutIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    sx={{ color: '#fff', fontWeight: 700, minWidth: 40, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {Math.round(scale * 100)}%
                  </Typography>
                  <Tooltip title="Zoom in">
                    <span>
                      <IconButton aria-label="Zoom in" size="small" onClick={() => zoomIn()} disabled={scale >= MAX_SCALE - 0.01} sx={toolButtonSx}>
                        <ZoomInIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Reset">
                    <span>
                      <IconButton aria-label="Reset zoom" size="small" onClick={() => resetTransform()} disabled={scale <= 1.01} sx={toolButtonSx}>
                        <ResetZoomIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </>
          )}
        </TransformWrapper>
      </Dialog>

      <AcceleratorProfileDialog hotspot={selected} onClose={() => setSelected(null)} />

      {isAdmin && (
        <AcceleratorHotspotForm
          open={!!form}
          posterKey={POSTER_KEY}
          hotspot={form?.hotspot ?? null}
          rect={form?.rect ?? null}
          onClose={() => setForm(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
          onRedraw={() => {
            setRedrawTarget(form?.hotspot ?? null);
            setForm(null);
          }}
        />
      )}
    </>
  );
}
